import { StyleSheet } from 'react-native-unistyles';
import EventListItemTeamItem, {
  EventListItemTeamItemFragment,
  EventListItemTeamRsvpFragment,
} from '@/components/Event/EventListItemTeamItem';
import Text from '@/components/UI/Text';
import { type FragmentOf, graphql, readFragment } from '@/gql/graphql';
import isEventStarted from '@/utils/isEventStarted';
import Box from '../UI/Box';

export const EventListItemTeamsFragment = graphql(`
  fragment EventListItemTeams on event {
    id
    home_team_id
    datetime
    event_teams(limit: 8, order_by: [{ team: { title: asc } }]) {
      event_id
      team_id
      score
      ...EventListItemTeamItem
    }
    event_teams_aggregate {
      aggregate {
        count
      }
    }
  }
`, [EventListItemTeamItemFragment]);

export const EventListItemTeamsRsvpFragment = graphql(`
  fragment EventListItemTeamsRsvp on event {
    id
    event_teams(limit: 8, order_by: [{ team: { title: asc } }]) {
      event_id
      team_id
      ...EventListItemTeamRsvp
    }
  }
`, [EventListItemTeamRsvpFragment]);

export default function EventListItemTeams({
  fragment,
  rsvpFragment,
}: {
  fragment: FragmentOf<typeof EventListItemTeamsFragment> | null | undefined;
  rsvpFragment?:
    | FragmentOf<typeof EventListItemTeamsRsvpFragment>
    | null
    | undefined;
}) {
  const event = readFragment(EventListItemTeamsFragment, fragment);
  const rsvpEvent = readFragment(EventListItemTeamsRsvpFragment, rsvpFragment);
  const eventTeamCount = event?.event_teams_aggregate?.aggregate?.count ?? 0;
  const findRsvpFragment = (teamId: string | null | undefined) =>
    rsvpEvent?.event_teams?.find(
      (rsvpEventTeam) => rsvpEventTeam.team_id === teamId,
    );

  if (eventTeamCount === 2) {
    const isPastGame = isEventStarted(event?.datetime);
    const [firstTeam, secondTeam] = event?.event_teams ?? [];
    const homeTeam =
      event?.event_teams?.find(
        (team) => team.team_id === event?.home_team_id,
      ) ?? secondTeam;
    const awayTeam = event?.event_teams?.find(
      (team) => team.team_id !== homeTeam?.team_id,
    );

    return (
      <Box flex py="lg" space="lg" style={style.container}>
        <EventListItemTeamItem
          reverse
          fragment={awayTeam ?? firstTeam}
          rsvpFragment={findRsvpFragment((awayTeam ?? firstTeam)?.team_id)}
          isPastGame={isPastGame}
        />
        <EventListItemTeamItem
          fragment={homeTeam}
          rsvpFragment={findRsvpFragment(homeTeam?.team_id)}
          isPastGame={isPastGame}
        />
      </Box>
    );
  }

  if (eventTeamCount === 1) {
    const eventTeam = event?.event_teams?.[0];

    return (
      <Box flex py="lg" style={style.container}>
        <Box>
          <EventListItemTeamItem
            fragment={eventTeam}
            rsvpFragment={findRsvpFragment(eventTeam?.team_id)}
            isPastGame={false}
          />
        </Box>
      </Box>
    );
  }

  if (eventTeamCount > 2) {
    return (
      <Box flex px="lg" py="lg" space="lg">
        <Text
          flexShrink={1}
          numberOfLines={1}
          style={style.avatarGroupItemText}
        >
          {eventTeamCount} Teams
        </Text>
        <Box space="md" style={style.avatarGroup} direction="horizontal">
          {event?.event_teams?.map((eventTeam) => (
            <EventListItemTeamItem
              key={eventTeam.team_id}
              fragment={eventTeam}
              rsvpFragment={findRsvpFragment(eventTeam.team_id)}
              isPastGame={false}
              showLabel={false}
              style={style.avatarGroupItem}
            />
          ))}
        </Box>
      </Box>
    );
  }

  return null;
}

const style = StyleSheet.create((theme) => ({
  container: {
    overflow: 'hidden',
  },
  avatarGroup: {
    flexShrink: 1,
  },
  avatarGroupItem: {
    paddingHorizontal: 0,
    flexShrink: 1,
  },
  avatarGroupItemText: {
    fontSize: {
      base: theme.fontSize.xl,
      lg: theme.fontSize['3xl'],
    },
  },
}));
